import { Directive, ElementRef, HostListener, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Formats a money input with thousands separators while keeping the bound model
 * a plain `number`.
 *
 * The input must be `type="text"` — a native number input cannot render `1,500,000`
 * and the browser rejects the separators outright.
 *
 * It replaces Angular's default value accessor for the host, so `[(ngModel)]` and
 * reactive forms both see the numeric value, never the masked string:
 *
 *   <input type="text" appThousandsMask [(ngModel)]="cfg.basePrice" />
 *
 * Supersedes the copy-pasted `value.replace(/\B(?=(\d{3})+(?!\d))/g, ',')` in
 * `props-rightbar.component.ts` and `edit-user-level.component.ts`.
 */
@Directive({
  selector: 'input[appThousandsMask]',
  standalone: true,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ThousandsMaskDirective),
      multi: true,
    },
  ],
})
export class ThousandsMaskDirective implements ControlValueAccessor {
  /** Decimal places the user may type. 0 keeps it whole-dollar. */
  @Input() maskDecimals = 0;

  private onChange: (value: number | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private disabled = false;

  constructor(private el: ElementRef<HTMLInputElement>) {}

  // --- ControlValueAccessor -------------------------------------------------

  writeValue(value: any): void {
    const next = this.format(this.toDigits(value));
    // Rewriting an unchanged value would throw the caret to the end mid-typing.
    if (this.el.nativeElement.value === next) return;
    this.el.nativeElement.value = next;
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.el.nativeElement.disabled = isDisabled;
  }

  // --- Masking --------------------------------------------------------------

  @HostListener('input')
  onInput(): void {
    if (this.disabled) return;
    const input = this.el.nativeElement;

    // Count the digits left of the caret so it can be restored after the
    // separators shift the string around — without this, editing anywhere but
    // the end throws the caret to the end on every keystroke.
    const caret = input.selectionStart ?? input.value.length;
    const digitsBeforeCaret = this.countDigits(input.value.slice(0, caret));

    const cleaned = this.toDigits(input.value);
    input.value = this.format(cleaned);
    this.restoreCaret(digitsBeforeCaret);

    this.onChange(cleaned === '' ? null : Number(cleaned));
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  /** Strip everything but digits and at most one decimal point. */
  private toDigits(value: any): string {
    if (value === null || value === undefined) return '';
    const raw = String(value).replace(/[^\d.]/g, '');
    if (!this.maskDecimals) return raw.replace(/\./g, '');

    const [whole, ...rest] = raw.split('.');
    if (!rest.length) return whole;
    return `${whole}.${rest.join('').slice(0, this.maskDecimals)}`;
  }

  /** Group the whole part in threes, leaving any decimal part alone. */
  private format(cleaned: string): string {
    if (cleaned === '') return '';
    const [whole, decimals] = cleaned.split('.');
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return decimals === undefined ? grouped : `${grouped}.${decimals}`;
  }

  private countDigits(text: string): number {
    return (text.match(/[\d.]/g) ?? []).length;
  }

  /** Put the caret back after the same number of digits it was before. */
  private restoreCaret(digitsBeforeCaret: number): void {
    const input = this.el.nativeElement;
    let seen = 0;
    let position = input.value.length;
    for (let i = 0; i < input.value.length; i++) {
      if (/[\d.]/.test(input.value[i])) seen++;
      if (seen === digitsBeforeCaret) {
        position = i + 1;
        break;
      }
    }
    if (digitsBeforeCaret === 0) position = 0;
    input.setSelectionRange(position, position);
  }
}
